import { say } from "./words.ts";

/**
 * A request's time limit on every browser the pages support (ADR-019's amendment of 2026-10-04). The browser's
 * own `AbortSignal.timeout` arrived in Safari 16.0, Chrome 103 and Firefox 100, above the game page's floor, so a
 * page that called it directly threw before sending anything on an older browser, and the request read as a
 * server that did not answer. This is the one place the client asks for it
 * (`tests/client-browser-floor.test.ts`).
 */

/**
 * A request's time limit: the signal to hand `fetch`, and `settle`, which the caller runs once the request has
 * ended, answered or not, so a limit kept on a timer never outlives its request.
 */
export interface TimeoutSignal {
  readonly signal: AbortSignal;
  readonly settle: () => void;
}

/**
 * Does nothing: what settling means where the browser keeps the limit itself.
 */
function nothingToSettle(): void {
  return undefined;
}

/**
 * A signal that aborts once a time has passed.
 *
 * @param ms The time limit in milliseconds.
 * @returns The browser's own timeout signal where it has one; otherwise a controller's signal aborted by a timer
 *   with a `TimeoutError`, as the browser's own is, the timer cleared by `settle`.
 * @example
 * const limit = timeoutSignal(8_000);
 * try {
 *   return await fetch(url, { signal: limit.signal });
 * } finally {
 *   limit.settle();
 * }
 */
export function timeoutSignal(ms: number): TimeoutSignal {
  if (typeof AbortSignal.timeout === "function") {
    return { signal: AbortSignal.timeout(ms), settle: nothingToSettle };
  }
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | null = setTimeout(() => {
    timer = null;
    controller.abort(new DOMException(say("request.tookTooLong"), "TimeoutError"));
  }, ms);
  return {
    signal: controller.signal,
    settle: () => {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    },
  };
}
